import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Empty, ui } from '../ui';
import { ApiError, useApi, useApiMutation } from '../api';
import { Screen, Table } from './shared';

/**
 * The decision queue. One, where there were three (#144).
 *
 * Reading it and acting on it, both here. The writes were batched server
 * actions in the Next app and are `POST /v1/admin/decisions/*`; each still
 * writes several tables in a single batch, because a half-applied decision is a
 * state somebody can actually be in.
 *
 * After a decision the queue refetches rather than being patched locally. A
 * decision can change what belongs in the queue in ways the client cannot
 * predict — approving a creator removes them, suspending one keeps them — so
 * asking the server is both simpler and the only way to be right.
 */

/**
 * The shared shape of "run a decision, then reload the queue".
 *
 * `method` is an argument rather than always `POST` because the review queue
 * (#112) acts on more than the three decision endpoints: putting an approved
 * and signed title on the store is `PATCH /admin/games/:id`, which is the
 * catalogue's own endpoint and not one this file gets to redefine. Everything
 * else about a decision — the busy row, the sentence back, the refetch of both
 * the queue and the overview — is identical, and was worth one parameter rather
 * than a second copy of this hook.
 *
 * **`decide` reports whether it worked**, which nothing needed until the review
 * queue started showing one item at a time (#114). That screen moves on to the
 * next item once a decision lands, and moving on after a *failed* one would
 * skip past something nobody has decided — the one thing a queue must never do.
 * The three table screens ignore the return, because they never move.
 */
function useDecision(queueKey: readonly unknown[]) {
  const call = useApiMutation();
  const cache = useQueryClient();
  const [message, setMessage] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  async function decide(
    id: string,
    path: string,
    body: unknown,
    method: 'POST' | 'PATCH' = 'POST',
  ): Promise<boolean> {
    setBusy(id);
    setMessage(null);
    try {
      const result = await call<{ ok: string }>(path, {
        method,
        body: JSON.stringify(body),
      });
      setMessage(result.ok);
      await cache.invalidateQueries({ queryKey: queueKey });
      // The overview's counts are now wrong too.
      await cache.invalidateQueries({ queryKey: ['admin', 'overview'] });
      return true;
    } catch (error) {
      setMessage(error instanceof ApiError ? error.message : 'That did not work. Try again.');
      return false;
    } finally {
      setBusy(null);
    }
  }

  return { decide, message, busy };
}

function Note({ message }: { message: string | null }): React.ReactElement | null {
  if (!message) return null;
  return (
    <p className="gb-body" role="status">
      {message}
    </p>
  );
}

/*
  `CurationQueueRoute` was here and is gone with the review queue.

  It listed titles in `submitted` or `in_review` with approve, reject and
  request-changes on each row. Curation itself stays - a title is still `draft`
  until somebody says otherwise, and section 5.5 still holds - but the decision
  is taken on the title's own page, `/admin/games/:productId`, which carries the
  whole of `CURATION_DECISIONS` and shows the gameplay link, the price and the
  art beside them. Deciding a game from a table row was always the thinner half
  of that.

  With titles created from the panel rather than submitted to it, a list of
  things waiting to be read had nothing left to list.
*/

/*
  `StudioRow`, `NewStudio` and `StudioQueueRoute` were here, and went with the
  studios (#144).

  The screen was every studio there was, with a registration form above it and
  the ones still waiting to be verified underneath. #126 had made it the only
  list that named a studio, because a studio's details, its people and its build
  tokens were each on a screen of their own by then.

  There are no studios. The build tokens moved to `/admin/tokens`, because a
  build still has to be uploaded.
*/

interface CreatorRow {
  id: string;
  handle: string;
  displayName: string;
  state: string;
  level: number;
  primaryLanguage: string | null;
}

/**
 * Creators.
 *
 * Suspended creators are listed as well as applicants, because a suspension is
 * a decision somebody may need to reverse and a queue that hides it makes that
 * unreachable. Suspension deletes nothing: section 19 keeps their history
 * intact so attribution already recorded stays resolvable for the ledger.
 */
export function CreatorQueueRoute(): React.ReactElement {
  const key = ['admin', 'queues', 'creators'] as const;
  const query = useApi<{ rows: CreatorRow[] }>(key, '/admin/decisions/queues/creators');
  const { decide, message, busy } = useDecision(key);
  const rows = query.data?.rows ?? [];

  return (
    <Screen title="Creators" aside={`${rows.length} needing a decision`} query={query}>
      {rows.length === 0 ? (
        <Empty>No applications and nobody suspended.</Empty>
      ) : (
        <Table head={['Creator', 'Handle', 'State', 'Level', '']}>
          {rows.map((row) => (
            <tr key={row.id}>
              <td>{row.displayName}</td>
              <td className={ui.numeric ?? ''}>@{row.handle}</td>
              <td>{row.state}</td>
              <td className={ui.numeric ?? ''}>{row.level}</td>
              <td>
                {(['approve', 'suspend'] as const).map((decision) => (
                  <button
                    key={decision}
                    type="button"
                    disabled={busy === row.id}
                    onClick={() =>
                      void decide(row.id, '/admin/decisions/creators', {
                        creatorProfileId: row.id,
                        decision,
                      })
                    }
                  >
                    {decision}
                  </button>
                ))}
              </td>
            </tr>
          ))}
        </Table>
      )}
      <Note message={message} />
    </Screen>
  );
}

/*
  Everything below this line was the unified review queue (#112, #114-#118),
  and it is gone.

  `/admin/queue` answered one question - *what is waiting for a decision today*
  - by folding seven kinds into one list: titles awaiting review, studios
  awaiting verification, creator applications, pitches, contracts that had
  stopped moving, titles ready to publish, and payout recipients who could not
  be paid. Six of those seven no longer exist at all. About eleven hundred lines here and two thousand in
  `apps/api/src/lib/admin/queue.ts` went into making those seven shapes read
  as one screen: the per-kind facts, the blocker lists, the organisation and
  submitter recaps, the action buttons and the card that drew them.

  Nothing arrives unasked any more. Studios are made from the panel rather than
  registering themselves (#125), titles are created there too (#127) rather than
  submitted by a studio (#142), there is no pitch inbox (#142), and the payout
  figure counted against a ledger that has been removed. A queue whose every
  source has been closed is a screen that can only say nothing is waiting, and
  one of those teaches everybody to stop opening it.

  What survives is above: one screen that is not a queue so much as a list with
  a decision on each row. It reads its own endpoint under
  `/admin/decisions/queues/creators`, never the unified one, which is why it
  outlives it. The studios' equivalent was beside it until #144.

  `PayoutQueueRoute` is the one that could not survive. Its only figure came
  from `GET /admin/decisions/queues` and counted recipients the ledger said were
  owed money; both are gone.
*/
